import { createTheme, alpha, type Theme } from "@mui/material/styles";
import type { Shadows } from "@mui/material/styles";
import type {} from "@mui/x-data-grid/themeAugmentation";

export type ColorMode = "light" | "dark";
export type Density = "comfortable" | "compact";

/**
 * HealInventory enterprise UI system. The thesis is restraint: a cool-slate neutral canvas carries
 * ~90% of the UI, a single confident blue is the only accent, and success/warning/error appear only
 * as state (never decoration). Numbers use tabular figures so columns align. Dark mode is a slate
 * night variant with the same discipline. Tokens live here; screens read them via the theme.
 */
const tokens = {
  light: {
    background: "#F6F7F9", // cool near-white, slight slate bias — chosen, not default grey
    paper: "#FFFFFF",
    surface2: "#FBFCFE",   // faint raised (table headers)
    textPrimary: "#0F1826",
    textSecondary: "#5A6572",
    textFaint: "#8B95A1",
    divider: "#E4E8EE",
    dividerStrong: "#D3D9E1",
    primary: { main: "#2F5AE0", light: "#5B8DEF", dark: "#234BC0", contrastText: "#FFFFFF" },
    secondary: { main: "#475569", light: "#64748B", dark: "#334155", contrastText: "#FFFFFF" }, // slate
    success: { main: "#17875A", light: "#5FBF93", dark: "#0F6644", contrastText: "#FFFFFF" },
    warning: { main: "#9A6410", light: "#C79433", dark: "#7A4E0A", contrastText: "#FFFFFF" },
    info: { main: "#2F5AE0", light: "#5B8DEF", dark: "#234BC0", contrastText: "#FFFFFF" },
    error: { main: "#B42318", light: "#DA7167", dark: "#8E1B12", contrastText: "#FFFFFF" },
  },
  dark: {
    background: "#0B1019",
    paper: "#141B27",
    surface2: "#182130",
    textPrimary: "#EAEEF4",
    textSecondary: "#9BA7B6",
    textFaint: "#6E7B8C",
    divider: "rgba(255,255,255,0.09)",
    dividerStrong: "rgba(255,255,255,0.16)",
    primary: { main: "#5B8DEF", light: "#88AEF5", dark: "#2F5AE0", contrastText: "#0A1220" },
    secondary: { main: "#94A3B8", light: "#B4C0D0", dark: "#64748B", contrastText: "#0B1019" },
    success: { main: "#3FB483", light: "#6ECBA3", dark: "#1F7A54", contrastText: "#06230F" },
    warning: { main: "#D9A441", light: "#E7C179", dark: "#A5791F", contrastText: "#241B05" },
    info: { main: "#5B8DEF", light: "#88AEF5", dark: "#2F5AE0", contrastText: "#08161F" },
    error: { main: "#E6685C", light: "#EF9089", dark: "#B4392E", contrastText: "#260B07" },
  },
} as const;

/**
 * Department colour coding — reserved for small accents only (a 6–8px dot, an avatar, a left-border),
 * never large fills. Access via DEPARTMENT_COLORS[key].
 */
export type DepartmentKey =
  | "opd" | "emergency" | "ipd" | "pharmacy" | "laboratory"
  | "radiology" | "billing" | "administration" | "icu";
export const DEPARTMENT_COLORS: Record<DepartmentKey, { main: string; label: string }> = {
  opd:            { main: "#2F5AE0", label: "OPD" },
  emergency:      { main: "#B42318", label: "Emergency" },
  ipd:            { main: "#7C5CD6", label: "IPD" },
  pharmacy:       { main: "#17875A", label: "Pharmacy" },
  laboratory:     { main: "#4F58C4", label: "Laboratory" },
  radiology:      { main: "#0E7C8B", label: "Radiology" },
  billing:        { main: "#B4700F", label: "Billing" },
  administration: { main: "#5A6572", label: "Administration" },
  icu:            { main: "#A81E30", label: "ICU" },
};

/** Deep-slate sidebar chrome — kept dark in both modes (blue-tinted active item). */
export type ShellPalette = {
  bg: string; text: string; border: string; brand: string;
  activeBg: string; activeText: string; hover: string;
};
export const SHELL: Record<ColorMode, ShellPalette> = {
  light: {
    bg: "#101B2D", text: "rgba(255,255,255,0.80)", border: "rgba(255,255,255,0.09)",
    brand: "#F6F7F9", activeBg: "rgba(91,141,239,0.20)", activeText: "#FFFFFF", hover: "rgba(255,255,255,0.06)",
  },
  dark: {
    bg: "#0C1421", text: "rgba(233,238,244,0.78)", border: "rgba(255,255,255,0.08)",
    brand: "#F6F7F9", activeBg: "rgba(91,141,239,0.18)", activeText: "#FFFFFF", hover: "rgba(255,255,255,0.05)",
  },
};

/** Soft, layered slate-tinted shadow ramp — refined depth instead of MUI's heavier defaults. */
function softShadows(): Shadows {
  const make = (e: number) =>
    `0px ${e}px ${e * 2}px ${-Math.ceil(e / 2)}px rgba(15,24,38,0.10), ` +
    `0px ${Math.ceil(e / 2)}px ${e}px ${-Math.ceil(e / 2)}px rgba(15,24,38,0.05)`;
  const arr: string[] = ["none"];
  for (let e = 1; e <= 24; e++) arr.push(make(e));
  return arr as unknown as Shadows;
}

export function buildTheme(mode: ColorMode = "light", density: Density = "comfortable"): Theme {
  const t = tokens[mode];
  const dense = density === "compact";
  return createTheme({
    palette: {
      mode,
      primary: t.primary,
      secondary: t.secondary,
      success: t.success,
      warning: t.warning,
      info: t.info,
      error: t.error,
      background: { default: t.background, paper: t.paper },
      text: { primary: t.textPrimary, secondary: t.textSecondary },
      divider: t.divider,
    },
    shape: { borderRadius: 8 },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      fontWeightMedium: 500,
      h4: { fontSize: "1.75rem", fontWeight: 700, letterSpacing: "-0.02em" }, // 28px
      h5: { fontSize: "1.375rem", fontWeight: 680, letterSpacing: "-0.02em" }, // 22px — page title
      h6: { fontSize: "1.25rem", fontWeight: 650, letterSpacing: "-0.01em" },  // 20px — section heading
      subtitle1: { fontSize: "1.0625rem", fontWeight: 600 },                   // 17px
      subtitle2: { fontSize: "0.875rem", fontWeight: 600 },                    // 14px — card labels
      body1: { fontSize: "0.9375rem" }, // 15px
      body2: { fontSize: "0.8125rem" }, // 13px — table data / secondary
      caption: { fontSize: "0.75rem" }, // 12px — helper text
      button: { textTransform: "none", fontWeight: 600 },
    },
    shadows: softShadows(),
    components: {
      // Tabular figures everywhere digits appear — the single biggest "enterprise data" signal.
      MuiCssBaseline: {
        styleOverrides: {
          body: { fontVariantNumeric: "tabular-nums" },
        },
      },
      MuiPaper: {
        styleOverrides: { root: { backgroundImage: "none" } },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 8 },
          sizeSmall: { paddingTop: 5, paddingBottom: 5 },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: ({ theme }) => ({ border: `1px solid ${theme.palette.divider}` }),
        },
      },
      MuiInputLabel: {
        styleOverrides: { root: { fontSize: "0.875rem" } },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: { borderColor: t.dividerStrong },
        },
      },
      // Plain MUI tables: quiet hairlines, uppercase muted headers on a faint fill.
      MuiTableHead: {
        styleOverrides: {
          root: { "& .MuiTableCell-head": { backgroundColor: t.surface2 } },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderColor: theme.palette.divider,
            ...(dense && { paddingTop: 6, paddingBottom: 6 }),
          }),
          head: {
            fontSize: "0.6875rem",
            fontWeight: 600,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: t.textSecondary,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: ({ theme }) => ({
            "&:hover": { backgroundColor: alpha(theme.palette.primary.main, mode === "dark" ? 0.08 : 0.035) },
          }),
        },
      },
      // Soft tinted status pills instead of loud solid Chips.
      MuiChip: {
        styleOverrides: {
          root: ({ ownerState, theme }) => {
            const c = ownerState.color;
            if (ownerState.variant !== "outlined" && c && c !== "default") {
              const pal = theme.palette[c] as { main: string; light: string; dark: string } | undefined;
              if (pal?.main) {
                return {
                  backgroundColor: alpha(pal.main, theme.palette.mode === "dark" ? 0.22 : 0.14),
                  color: theme.palette.mode === "dark" ? pal.light : pal.dark,
                  fontWeight: 600,
                  borderColor: "transparent",
                };
              }
            }
            return {};
          },
        },
      },
      // App-wide DataGrid styling: hairline frame, quiet uppercase headers, calm hover, no focus rings.
      MuiDataGrid: {
        defaultProps: { density: dense ? "compact" : "standard" },
        styleOverrides: {
          root: ({ theme }) => ({
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 10,
            fontSize: "0.8125rem",
            backgroundColor: theme.palette.background.paper,
            "--DataGrid-rowBorderColor": theme.palette.divider,
            "--DataGrid-containerBackground": t.surface2,
            "& .MuiDataGrid-columnHeaderTitle": {
              fontSize: "0.6875rem",
              fontWeight: 600,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              color: theme.palette.text.secondary,
            },
            "& .MuiDataGrid-columnSeparator": { display: "none" },
            "& .MuiDataGrid-cell": { borderColor: theme.palette.divider },
            // Row-action icons, centred. The grid centres cell content with
            // `line-height: <row height>` rather than flexbox, and only switches a cell to
            // `display:flex` when the column opts in — which the built-in actions/boolean/checkbox
            // column types do and a plain column with a renderCell does not. Every listing screen
            // builds its edit/delete column as a plain column named "actions", so its IconButton
            // sat on that tall line box's baseline and rode up towards the top of the row.
            // Flexing the cell is the fix MUI intends, and it is safe to do globally *here*
            // because this column holds no text: nothing loses the cell's text-overflow ellipsis,
            // which is why the same rule is not applied to cells at large.
            '& .MuiDataGrid-cell[data-field="actions"]': {
              display: "flex",
              alignItems: "center",
              lineHeight: "inherit",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: alpha(theme.palette.primary.main, mode === "dark" ? 0.08 : 0.035),
            },
            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
              outline: "none",
            },
            "& .MuiDataGrid-footerContainer": { borderTopColor: theme.palette.divider },
          }),
        },
      },
    },
  });
}
